<template>
  <v-sheet class="transparent">
    <v-sheet
        class="ma-4 text-center d-flex align-center justify-center bg-img"
        min-height="250px"
        rounded
    >
      <v-row align="center" class="fill-height" justify="center">
        <v-col cols="2"></v-col>
        <v-col cols="8">
          <v-sheet class="mt-8 mb-3 transparent font-weight-bold" style="font-size: 2rem; color: black ">
            Phosphosite Function. Powered by Integrative Intelligence
            <div style="font-size: 1.2rem; color: black ">PTMax combines AI-based literature mining with multi-omics
              integration to deliver site-level functional insight at scale
            </div>
          </v-sheet>
          <v-sheet class="pa-2 mt-3 mb-6 transparent">
            <v-row align="center" no-gutters>
              <v-select
                  v-model="searchMode"
                  :items="['Single Search', 'Batch Search']"
                  dense
                  hide-details
                  outlined
                  style="max-width: 160px"
              />

              <v-autocomplete
                  v-if="searchMode === 'Single Search'"
                  v-model="singleSearchInput"
                  :items="suggestions"
                  :loading="loading"
                  :return-object="false"
                  :search-input.sync="searchText"
                  clearable
                  dense
                  hide-details
                  item-text="text"
                  item-value="value"
                  label="Substrate (gene symbol, UniProt, or Ensembl protein) or substrate:site"
                  outlined
                  style="background-color: white;"
                  @change="onSelectAndSubmit"
                  @update:search-input="onSearch"
              >


                <template v-slot:item="data">
                  <div v-html="highlightMatch(data.item.text)"></div>
                </template>
              </v-autocomplete>

              <v-textarea
                  v-else
                  v-model="batchSearchInput"
                  class="flex-grow-1 mx-2"
                  clearable
                  dense
                  hint="Example:
AKT1S1	S203
VCL	Y822
(value is optional; required for GSEA)"
                  label="Substrate, site, and optional value separated by space or tab"
                  outlined
                  persistent-hint
                  style="background-color: white;height: 100%;max-height: 200px; white-space: pre-line;"
              />

              <v-btn :disabled="!isSearchEnabled" class="ml-2 text-capitalize" color="primary" height="40"
                     @click="submitSearch">
                Search
              </v-btn>
            </v-row>

            <v-row
                v-if="searchMode === 'Single Search'"
                class="mt-6"
                no-gutters
                style="font-size: 13px;"
            >
              <v-col class="d-flex align-center" cols="12" style="padding-left: 170px;">
                <span class="font-weight-bold mr-2">Examples:</span>
                <span
                    v-for="(item, idx) in ['MAPK3','P27361','ENSP00000367263', 'MAPK3:Y204','P27361:Y204','ENSP00000367263:S135']"
                    :key="item"
                >
              <a
                  class="text-decoration-underline font-weight-medium mr-2"
                  href="javascript:void(0);"
                  style="cursor: pointer;"
                  @click="handleExampleClick(item)"
              >
                {{ item }}
              </a>
            </span>
              </v-col>
            </v-row>

            <v-row
                v-if="searchMode === 'Batch Search'"
                class="mt-6"
                no-gutters
                style="font-size: 13px;"
            >
              <v-col class="d-flex align-center" cols="12" style="padding-left: 170px;">
                <span class="font-weight-bold mr-2">Examples:</span>
                <v-btn dense outlined x-small @click="loadExample(1)">1</v-btn>
                <v-btn class="ml-2" dense outlined x-small @click="loadExample(2)">2</v-btn>
                <v-btn class="ml-2" dense outlined x-small @click="loadExample(3)">3</v-btn>
              </v-col>
            </v-row>

          </v-sheet>
        </v-col>
        <v-col cols="2"></v-col>
      </v-row>


    </v-sheet>

    <v-sheet class="transparent" min-height="300px">
      <v-row class="mx-2 my-2">
        <v-col cols="6">
          <v-row no-gutters>
            <v-col cols="12">
              <v-card :elevation="total_elevation" class="pa-4 ">
                <v-sheet class="font-weight-bold mb-2" style="font-size: 1.3rem">
                  <v-icon color="teal" dark>mdi-book</v-icon>
                  Introduction
                </v-sheet>
                <v-divider/>
                <v-sheet class=" py-5 overflow-y-auto" max-height="200" min-height="200" style="font-size: 1rem">
                  PTMax is a framework that integrates <b>AI-based mining</b> of biomedical literature text and pathway
                  figures with multi-omics data to deliver site-resolved functional insight at scale. It provides
                  comprehensive, structured site-level annotation, facilitates prioritization of understudied
                  phosphosites, and supports literature-informed, site-resolved pathway analysis, thereby enabling
                  systematic synthesis of phosphosite-level functional insight from phosphoproteomic data.
                  <div class="text-left  mt-8 text-body-2" >
                    <v-icon class="mr-1 " small>mdi-information-outline</v-icon>
                    To get the best experience, please use the latest version of Chrome, Edge, or Firefox.
                  </div>
                </v-sheet>
              </v-card>
            </v-col>

            <v-col class="pr-3" cols="6">
              <v-card :elevation="total_elevation" class="pa-4 mt-3">
                <v-sheet class="font-weight-bold mb-2" style="font-size: 1.3rem">
                  <v-icon color="teal" dark>mdi-database</v-icon>
                  Related Database
                </v-sheet>
                <v-divider/>
                <v-sheet class="news-board  overflow-y-auto" max-height="150" min-height="150" style="flex-grow: 1;">
                  <v-timeline dense>
                    <v-timeline-item
                        v-for="(item, idx) in related_database"
                        :key="idx"
                        class="news-item"
                        color="teal"
                        icon="mdi-database"
                        small
                    >
                      <div>
                        <!--                    <div class="text-caption grey&#45;&#45;text">{{ item.date }}</div>-->
                        <a :href="item.link" class="text-body-1 news-text" target="_blank"
                           style="text-decoration: none; color: darkslategrey">{{ item.name }} <v-icon x-small>mdi-share</v-icon></a>
                      </div>

                    </v-timeline-item>
                  </v-timeline>
                </v-sheet>
              </v-card>
            </v-col>
            <v-col cols="6">
              <v-card
                  :elevation="total_elevation"
                  class="pa-4 d-flex flex-column mt-3"

              >
                <v-sheet class="font-weight-bold mb-2" style="font-size: 1.3rem">
                  <v-icon color="teal" dark>mdi-bullhorn-outline</v-icon>
                  News
                </v-sheet>
                <v-divider/>

                <v-sheet class="news-board  overflow-y-auto" max-height="150" min-height="150" style="flex-grow: 1;">
                  <v-timeline dense>
                    <v-timeline-item
                        v-for="(item, idx) in news"
                        :key="idx"
                        class="news-item"
                        color="teal"
                        icon="mdi-bullhorn-outline"
                        small
                    >
                      <div>
                        <div class="text-caption grey--text">{{ item.date }}</div>
                        <div class="text-body-1 news-text">{{ item.text }}</div>
                      </div>
                    </v-timeline-item>
                  </v-timeline>
                </v-sheet>
              </v-card>
            </v-col>
          </v-row>

        </v-col>
        <v-col cols="6">
          <v-carousel cycle progress>
            <v-carousel-item
                cover
                src="https://www.ptmax.org/img/fp1.png"
            ></v-carousel-item>
            <v-carousel-item
                cover
                src="https://www.ptmax.org/img/fp2.png"
            ></v-carousel-item>
            <v-carousel-item
                cover
                src="https://www.ptmax.org/img/fp3.png"
            ></v-carousel-item>
            <v-carousel-item
                cover
                src="https://www.ptmax.org/img/fp4.png"
            ></v-carousel-item>
            <v-carousel-item
                cover
                src="https://www.ptmax.org/img/fp5.png"
            ></v-carousel-item>
            <v-carousel-item
                cover
                src="https://www.ptmax.org/img/fp6.png"
            ></v-carousel-item>

          </v-carousel>
        </v-col>

      </v-row>
    </v-sheet>
    <!--    <v-sheet class="overflow-hidden" height="260px">-->
    <!--      <v-row class="mx-6 my-2" style="height: 100%; align-content: flex-start;">-->

    <!--        <v-col class="py-0" cols="12">-->

    <!--        </v-col>-->
    <!--      </v-row>-->
    <!--    </v-sheet>-->
  </v-sheet>

</template>

<script>
import Axios from "axios";
import _ from "lodash";
import {examples, related_database, news} from '@/config/exampleList'
import {apiprefix} from "@/main";

export default {
  name: "HomePage",
  data() {
    return {
      curr_year: "",
      singleSearchInput: "",
      batchSearchInput: "",
      searchMode: "Single Search",
      searchText: "",
      suggestions: [],
      cachedGeneSuggestions: [],
      cachedSiteSuggestions: {},
      lastQueriedGene: "",
      loading: false,
      total_elevation: 2,
    };
  },
  methods: {
    loadExample(id) {
      this.batchSearchInput = examples[id] || '';
    },
    onSelectAndSubmit(val) {
      if (!val) return;

      this.singleSearchInput = val;
      this.searchText = val;

      this.$nextTick(() => {
        this.submitSearch();
      });
    },
    onSearch: _.debounce(function (val) {
      const input = (val || "").trim();
      this.searchText = input;

      if (!input || input.length < 2) {
        this.suggestions = [];
        return;
      }

      if (input.startsWith("ENSP") && input.length < 13) {
        this.suggestions = [];
        return;
      }

      if (input.includes(":")) {
        const [gene, partialSite] = input.split(":");

        if (this.cachedSiteSuggestions[gene]) {
          const sites = this.cachedSiteSuggestions[gene];
          this.suggestions = sites
              .filter(s => s.toUpperCase().includes(partialSite.toUpperCase()))
              .map(s => ({text: `${gene}:${s}`, value: `${gene}:${s}`}));
          return;
        }

        Axios.get(`${apiprefix}/api/site_suggestions?gene=${gene}`)
            .then(res => {
              const sites = res.data.sites || [];
              this.cachedSiteSuggestions[gene] = sites;

              this.suggestions = sites
                  .filter(s => s.toUpperCase().includes(partialSite.toUpperCase()))
                  .map(s => ({text: `${gene}:${s}`, value: `${gene}:${s}`}));
            })
            .catch(() => {
              this.suggestions = [];
            });

        return;
      }

      if (this.cachedGeneSuggestions[input]) {
        this.suggestions = this.cachedGeneSuggestions[input];
        return;
      }

      Axios.get(`${apiprefix}/api/gene_suggestions?query=${input}`)
          .then(res => {
            const genes = res.data.genes || [];
            const formatted = genes.map(g => ({text: g, value: g}));

            this.cachedGeneSuggestions[input] = formatted;
            this.suggestions = formatted;
          })
          .catch(() => {
            this.suggestions = [];
          });

    }, 200),

    submitSearch() {
      if (this.searchMode !== "Single Search") {
        this.handleBatchSearch();
        return;
      }

      if (!this.singleSearchInput) {
        this.$toast?.error?.("Please select a valid suggestion");
        return;
      }

      const query = this.singleSearchInput.trim();

      this.$router.push({
        path: "/single-search",
        query: {
          item: query.toUpperCase(),
          ptm_type: "phosphorylation"
        }
      }).catch(() => {
      });
    },


    handleBatchSearch() {
      const input = this.batchSearchInput.trim();
      if (!input) return;

      const formatted = input
          .split(/[\n,]+/)
          .map(l => l.trim())
          .filter(Boolean)
          .map(l => {
            const p = l.split(/\s+/);
            return p.length >= 2 ? `${p[0].toUpperCase()}_${p[1].toUpperCase()}` : null;
          })
          .filter(Boolean);

      this.$router.push({
        path: "/batch-search",
        query: {
          input: input,
          formatted: JSON.stringify(formatted)
        }
        // query: {
        //   input: encodeURIComponent(input),
        //   formatted: JSON.stringify(formatted)
        // }
      }).catch(() => {
      });
    },

    handleExampleClick(example) {
      this.singleSearchInput = example;
      this.submitSearch();
    },

    highlightMatch(text) {
      if (!this.searchText) return text;
      const key = this.searchText.split(":")[1] || this.searchText;
      return text.replace(new RegExp(`(${key})`, "gi"), "<b>$1</b>");
    }
  },
  computed: {
    isSearchEnabled() {
      if (this.searchMode === "Single Search") {
        return this.suggestions.some(
            s => s.value === this.singleSearchInput
        );
      }
      return this.batchSearchInput.trim().length > 0;
    },
    related_database() {
      return related_database
    },
    news() {
      return news
    }
  },
  beforeMount() {

  }

};
</script>


<style lang="scss" scoped>
.statistics-card {
  min-height: 400px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.example-text {
  text-align: left;
  margin-left: 4px;
  font-size: 0.875rem;
  color: black;
}

.hovered-card {
  background-color: #f5faff;
  transition: background-color 0.3s ease;
}

.bg-img {
  position: relative;
  background-image: url("@/assets/bg1.png");
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  overflow: hidden;
}

.bg-img::before {
  content: "";
  position: absolute;
  inset: 0;
  background: rgba(204, 214, 226, 0.55);
  mix-blend-mode: overlay;
  pointer-events: none;
}


.introduction-text {
  line-height: 1.6;
}

.news-board {
  flex: 1;
  overflow-y: auto;
}

.news-item {
  padding-top: 4px !important;
  padding-bottom: 4px !important;
}

.news-text {
  line-height: 1.4;
}

.news-board::-webkit-scrollbar {
  width: 6px;
}

.news-board::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.25);
  border-radius: 3px;
}


</style>
